import { IPathwayActivity, ActivityType } from "app/_modules/pathways/entities";
import { ACTIVITY_STATUS } from "../constants/activityStatus";
import { ACTIVITY_CARD_STATUS, TActivityCardStatus, MEDICATION_BADGE_STATUS, TMedicationBadgeStatus } from "../constants/activityCardStatus";
import { TReadingTone } from "../constants/activityReadingTone";

export type TActivityBucket = 'daily' | 'morning' | 'afternoon' | 'evening';
type TTimedBucket = Exclude<TActivityBucket, 'daily'>;

const BUCKET_BOUNDARIES: Record<TTimedBucket, { start: number; end: number }> = {
    morning: { start: 5, end: 12 },
    afternoon: { start: 12, end: 18 },
    evening: { start: 18, end: 24 },
};

const BUCKET_ORDER: Record<TActivityBucket, number> = { morning: 0, afternoon: 1, evening: 2, daily: 3 };

const BUCKET_START_LABEL: Record<TTimedBucket, string> = {
    morning: '05:00',
    afternoon: '12:00',
    evening: '18:00',
};

const isTimedBucket = (bucket: TActivityBucket): bucket is TTimedBucket => bucket !== 'daily';

export const getBucketAvailableFromLabel = (bucket: TActivityBucket): string =>
    isTimedBucket(bucket) ? BUCKET_START_LABEL[bucket] : '';

export const getUnlockLabel = (
    activity: IPathwayActivity,
    bucket: TActivityBucket,
): { key: string; params?: Record<string, string> } => {
    if (activity.unlockAt) {
        return { key: 'screens.activities.card.availableFrom', params: { time: formatTime(activity.unlockAt) } };
    }
    if (activity.unlocksAfterActivityId) {
        return { key: 'screens.activities.card.availableAfterFirst' };
    }
    return { key: 'screens.activities.card.availableFrom', params: { time: getBucketAvailableFromLabel(bucket) } };
};

export const mapActivityToCardStatus = (activity: IPathwayActivity, bucket: TActivityBucket): TActivityCardStatus => {
    switch (activity.status) {
        case ACTIVITY_STATUS.COMPLETED:
            return activity.standalone ? ACTIVITY_CARD_STATUS.EXTRA : ACTIVITY_CARD_STATUS.COMPLETED;
        case ACTIVITY_STATUS.MISSED:
        case ACTIVITY_STATUS.NO_SHOW:
        case ACTIVITY_STATUS.CANCELLED:
            return ACTIVITY_CARD_STATUS.NOT_COMPLETED;
        case ACTIVITY_STATUS.DISABLED:
            return ACTIVITY_CARD_STATUS.LOCKED;
        case ACTIVITY_STATUS.SCHEDULED:
        default: {
            if (activity.activityType === ActivityType.PATHWAY_DRUG) {
                return ACTIVITY_CARD_STATUS.TODO;
            }
            if (isTimedBucket(bucket) && activity.isFlexible !== true) {
                const currentHour = new Date().getHours();
                if (currentHour >= BUCKET_BOUNDARIES[bucket].end) {
                    return ACTIVITY_CARD_STATUS.NOT_COMPLETED;
                }
                if (currentHour < BUCKET_BOUNDARIES[bucket].start) {
                    return ACTIVITY_CARD_STATUS.LOCKED;
                }
            }
            return ACTIVITY_CARD_STATUS.TODO;
        }
    }
};

const formatTime = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatDayMonth = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long' }).format(date);
};

export const getMedicationBadgeStatus = (
    activity: IPathwayActivity,
): TMedicationBadgeStatus | null => {
    if (!activity.drugIntakeStatus || activity.drugIntakeStatus === 'to_take') {
        return null;
    }
    return activity.drugIntakeStatus;
};

export const hasRepeatRequest = (activity: IPathwayActivity): boolean =>
    !!activity.pendingAlert && !activity.pendingAlert.isRemeasurement;

export const isNearDeadline = (
    activity: IPathwayActivity,
    bucket: TActivityBucket,
    cardStatus: TActivityCardStatus,
): boolean => {
    if (cardStatus !== ACTIVITY_CARD_STATUS.TODO) return false;
    if (activity.activityType === ActivityType.PATHWAY_DRUG) return false;
    if (!isTimedBucket(bucket) || activity.isFlexible === true) return false;
    const now = new Date();
    const minutesToEnd = BUCKET_BOUNDARIES[bucket].end * 60 - (now.getHours() * 60 + now.getMinutes());
    return minutesToEnd > 0 && minutesToEnd <= 60;
};

export const compareTodoOrder = (
    firstItem: { activity: IPathwayActivity; bucket: TActivityBucket },
    secondItem: { activity: IPathwayActivity; bucket: TActivityBucket },
): number => {
    const tierOf = (item: { activity: IPathwayActivity; bucket: TActivityBucket }): number => {
        if (!isTimedBucket(item.bucket)) return 2;
        return item.activity.isFlexible === true ? 1 : 0;
    };

    const firstTier = tierOf(firstItem);
    const secondTier = tierOf(secondItem);
    if (firstTier !== secondTier) return firstTier - secondTier;

    if (firstTier === 0) {
        const firstBucketEnd = BUCKET_BOUNDARIES[firstItem.bucket as TTimedBucket].end;
        const secondBucketEnd = BUCKET_BOUNDARIES[secondItem.bucket as TTimedBucket].end;
        if (firstBucketEnd !== secondBucketEnd) return firstBucketEnd - secondBucketEnd;
    }
    if (firstTier <= 1) {
        return BUCKET_ORDER[firstItem.bucket] - BUCKET_ORDER[secondItem.bucket];
    }
    return 0;
};

export const mapCardStatusToReadingTone = (cardStatus: TActivityCardStatus): TReadingTone => {
    if (cardStatus === ACTIVITY_CARD_STATUS.NOT_COMPLETED) return 'missed';
    if (cardStatus === ACTIVITY_CARD_STATUS.EXTRA) return 'extra';
    return 'done';
};

export const getActionLabelKey = (activityType: string): string => {
    switch (activityType) {
        case ActivityType.PATHWAY_VITAL_PARAMETER:
            return 'screens.activities.card.action.measure';
        case ActivityType.PATHWAY_QUESTIONNAIRE:
            return 'screens.activities.card.action.answer';
        case ActivityType.PATHWAY_DRUG:
            return 'screens.activities.card.action.confirm';
        default:
            return 'screens.activities.card.action.default';
    }
};

export const getSupportLine = (
    activity: IPathwayActivity,
    bucket: TActivityBucket,
    cardStatus: TActivityCardStatus
): { key: string; params?: Record<string, string> } => {
    if (hasRepeatRequest(activity)) {
        return {
            key: 'screens.activities.card.repeat.measuredAt',
            params: { date: formatDayMonth(activity.completedAt), time: formatTime(activity.completedAt) },
        };
    }
    if (cardStatus === ACTIVITY_CARD_STATUS.EXTRA) {
        return { key: 'screens.activities.card.supportLine.extra', params: { time: formatTime(activity.completedAt) } };
    }
    if (cardStatus === ACTIVITY_CARD_STATUS.COMPLETED) {
        if (activity.activityType === ActivityType.PATHWAY_DRUG) {
            return { key: 'screens.activities.card.supportLine.registeredAt', params: { time: formatTime(activity.completedAt) } };
        }
        return { key: 'screens.activities.card.supportLine.completedAt', params: { time: formatTime(activity.completedAt) } };
    }
    if (cardStatus === ACTIVITY_CARD_STATUS.NOT_COMPLETED) {
        if (isTimedBucket(bucket)) {
            return { key: `screens.activities.card.supportLine.notCompletedSlot.${bucket}` };
        }
        return { key: 'screens.activities.card.supportLine.notCompleted' };
    }
    if (activity.activityType === ActivityType.PATHWAY_QUESTIONNAIRE) {
        return { key: 'screens.activities.card.supportLine.questionnaire' };
    }
    if (!isTimedBucket(bucket)) {
        return { key: '' };
    }
    if (isNearDeadline(activity, bucket, cardStatus)) {
        return { key: `screens.activities.card.supportLine.nearDeadline.${bucket}` };
    }
    const isFixed = activity.isFlexible !== true;
    return { key: `screens.activities.card.supportLine.${isFixed ? 'fixed' : 'flexible'}.${bucket}` };
};
